import { getApiBase } from "./api-env";
import { getAccessToken, refreshAccessToken } from "./auth";

import type { Screen } from "@/lib/training/types";

/**
 * RN-facing training API client — mirrors the compliance-api request flow.
 *
 * Course content lives in the database, not in the app: the bundle is fetched
 * whole and rendered, and its media is streamed from CDN URLs carried in the
 * content. Nothing here is compiled into the binary.
 */
async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getAccessToken();
    // Only declare a JSON body when there IS one: Fastify rejects an empty body
    // with `Content-Type: application/json` ("Body cannot be empty"), and the
    // course-complete POST carries no payload.
    const headers: Record<string, string> = {
        ...(options?.body ? { "Content-Type": "application/json" } : {}),
        ...(options?.headers as Record<string, string>)
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    let res = await fetch(`${getApiBase()}${path}`, { ...options, headers });

    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            res = await fetch(`${getApiBase()}${path}`, { ...options, headers });
        } else {
            throw new Error("Couldn't refresh your session. Please try again.");
        }
    }

    if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
            error?: { message?: string };
        } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
    }

    return (await res.json()) as T;
}

export interface TrainingCourse {
    /** Permanent — completions are recorded against this. */
    slug: string;
    title: string;
    label?: string;
    estMin?: number;
    screens: Screen[];
}

export interface TrainingBundle {
    id: number;
    slug: string;
    title: string;
    version: number;
    content: { bundleTitle: string; courses: TrainingCourse[] };
}

export interface BundleProgress {
    bundleId: number;
    total: number;
    completed: number;
    completedSlugs: string[];
    isComplete: boolean;
}

/**
 * A yearly bundle's renewal window (the scope-of-practice refresher). The
 * server lists it and accepts completions only while `open`. Null (or absent,
 * from an older server) for a one-time bundle like orientation.
 */
export interface BundleWindow {
    open: boolean;
    opensOn: string | null;
    /** Last valid day of the current record; after finishing, the next one. */
    dueDate: string | null;
}

/**
 * Every active bundle with this nurse's progress through each — the training
 * list. The app shows one card per bundle, so it cannot assume a single slug.
 */
export function listTrainingBundles(rnId: number) {
    return request<{
        bundles: {
            bundle: TrainingBundle;
            progress: BundleProgress;
            window?: BundleWindow | null;
        }[];
    }>(`/rn/${rnId}/training/bundles`);
}

/** The active bundle plus this nurse's progress through it. */
export function getTrainingBundle(rnId: number, slug: string) {
    return request<{
        bundle: TrainingBundle;
        progress: BundleProgress;
        window?: BundleWindow | null;
    }>(`/rn/${rnId}/training/bundles/${slug}`);
}

/**
 * Marks one course finished. Idempotent server-side, so a retry after a flaky
 * connection is safe. Finishing the last course finalizes the bundle and
 * creates the compliance record.
 */
export function completeTrainingCourse(rnId: number, slug: string, courseSlug: string) {
    return request<{ progress: BundleProgress }>(
        `/rn/${rnId}/training/bundles/${slug}/courses/${courseSlug}/complete`,
        { method: "POST" }
    );
}

/** The bundle every RN must finish during onboarding. */
export const ORIENTATION_BUNDLE_SLUG = "rn-initial-orientation";
